/**
 * Assembles everything an export needs, once, so the three output formats
 * cannot disagree with each other about what happened in a period.
 */

import { auditInRange, verifyChain, type ChainVerification } from '@/db/audit';
import {
  invoicesInPeriod,
  ordersInPeriod,
  shiftsInPeriod,
  tripsInPeriod,
} from '@/db/repositories';
import { addDays, describePeriod, nowIso } from '@/domain/dates';
import { vatFromGross } from '@/domain/money';
import type {
  Address,
  AppSettings,
  AuditEntry,
  CompanySettings,
  Invoice,
  IsoDate,
  Order,
  Passenger,
  Shift,
  Trip,
  Vehicle,
} from '@/domain/types';
import { reconcileShift, type ShiftReconciliation } from '@/features/trips/tripService';
import type { AccountRecord } from '@/crypto/vault';

export interface ExportTotals {
  tripCount: number;
  distanceMeters: number;
  grossCents: number;
  vatCents: number;
  netCents: number;
  tipCents: number;
  byVatRate: Array<{ ratePercent: number; grossCents: number; vatCents: number; netCents: number }>;
  byPaymentMethod: Array<{ method: string; grossCents: number; count: number }>;
}

export interface ExportDataset {
  from: IsoDate;
  to: IsoDate;
  periodLabel: string;
  generatedAt: string;

  company: CompanySettings;
  app: AppSettings;
  driver: Pick<AccountRecord, 'id' | 'displayName' | 'driverCode' | 'username'>;

  trips: Trip[];
  shifts: Shift[];
  reconciliations: ShiftReconciliation[];
  orders: Order[];
  invoices: Invoice[];

  passengers: Map<string, Passenger>;
  addresses: Map<string, Address>;
  vehicles: Map<string, Vehicle>;

  /** Every audit entry that touched the period, in chain order. */
  auditEntries: AuditEntry[];
  /** Audit entries grouped by the record they belong to. */
  auditByEntity: Map<string, AuditEntry[]>;
  chain: ChainVerification;

  totals: ExportTotals;
}

export interface BuildDatasetParams {
  dataKey: CryptoKey;
  from: IsoDate;
  to: IsoDate;
  company: CompanySettings;
  app: AppSettings;
  driver: AccountRecord;
  passengers: readonly Passenger[];
  addresses: readonly Address[];
  vehicles: readonly Vehicle[];
}

export async function buildExportDataset(params: BuildDatasetParams): Promise<ExportDataset> {
  const { dataKey, from, to } = params;

  const [trips, shifts, orders, invoices, chain] = await Promise.all([
    tripsInPeriod(dataKey, from, to),
    shiftsInPeriod(dataKey, from, to),
    ordersInPeriod(dataKey, from, to),
    invoicesInPeriod(dataKey, from, to),
    verifyChain(dataKey),
  ]);

  const reconciliations = await Promise.all(
    shifts.map((shift) => reconcileShift(dataKey, shift, params.app.reconciliationToleranceMeters)),
  );

  // The audit range runs to the moment of export rather than to the period
  // end: a correction made in November to a July trip belongs in the July
  // export, because that is the change history the reader has to see.
  const auditEntries = await auditInRange(dataKey, `${from}T00:00:00.000Z`, nowIso());

  const relevantIds = new Set<string>([
    ...trips.map((t) => t.id),
    ...shifts.map((s) => s.id),
    ...orders.map((o) => o.id),
    ...invoices.map((i) => i.id),
  ]);

  const auditByEntity = new Map<string, AuditEntry[]>();
  const scoped: AuditEntry[] = [];
  for (const entry of auditEntries) {
    if (!relevantIds.has(entry.entityId)) continue;
    scoped.push(entry);
    const list = auditByEntity.get(entry.entityId);
    if (list) list.push(entry);
    else auditByEntity.set(entry.entityId, [entry]);
  }

  return {
    from,
    to,
    periodLabel: describePeriod(from, to),
    generatedAt: nowIso(),
    company: params.company,
    app: params.app,
    driver: {
      id: params.driver.id,
      displayName: params.driver.displayName,
      driverCode: params.driver.driverCode,
      username: params.driver.username,
    },
    trips,
    shifts,
    reconciliations,
    orders,
    invoices,
    passengers: new Map(params.passengers.map((p) => [p.id, p])),
    addresses: new Map(params.addresses.map((a) => [a.id, a])),
    vehicles: new Map(params.vehicles.map((v) => [v.id, v])),
    auditEntries: scoped,
    auditByEntity,
    chain,
    totals: computeTotals(trips),
  };
}

export function computeTotals(trips: readonly Trip[]): ExportTotals {
  const byRate = new Map<number, { grossCents: number; vatCents: number; netCents: number }>();
  const byMethod = new Map<string, { grossCents: number; count: number }>();

  let distanceMeters = 0;
  let grossCents = 0;
  let vatCents = 0;
  let tipCents = 0;

  for (const trip of trips) {
    distanceMeters += trip.distanceMeters;
    grossCents += trip.fareGrossCents;
    tipCents += trip.tipCents;

    const tripVat = vatFromGross(trip.fareGrossCents, trip.vatRatePercent);
    vatCents += tripVat;

    const rateBucket = byRate.get(trip.vatRatePercent) ?? {
      grossCents: 0,
      vatCents: 0,
      netCents: 0,
    };
    rateBucket.grossCents += trip.fareGrossCents;
    rateBucket.vatCents += tripVat;
    rateBucket.netCents += trip.fareGrossCents - tripVat;
    byRate.set(trip.vatRatePercent, rateBucket);

    const methodBucket = byMethod.get(trip.paymentMethod) ?? { grossCents: 0, count: 0 };
    methodBucket.grossCents += trip.fareGrossCents;
    methodBucket.count += 1;
    byMethod.set(trip.paymentMethod, methodBucket);
  }

  return {
    tripCount: trips.length,
    distanceMeters,
    grossCents,
    vatCents,
    netCents: grossCents - vatCents,
    tipCents,
    byVatRate: [...byRate.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([ratePercent, values]) => ({ ratePercent, ...values })),
    byPaymentMethod: [...byMethod.entries()]
      .sort((a, b) => b[1].grossCents - a[1].grossCents)
      .map(([method, values]) => ({ method, ...values })),
  };
}

/** Convenience: whole calendar year. */
export function yearRange(year: number): { from: IsoDate; to: IsoDate } {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** Convenience: whole calendar month from a `YYYY-MM` bucket. */
export function monthRange(bucket: string): { from: IsoDate; to: IsoDate } {
  const from = `${bucket}-01`;
  const next = new Date(`${from}T00:00:00Z`);
  next.setUTCMonth(next.getUTCMonth() + 1);
  return { from, to: addDays(next.toISOString().slice(0, 10), -1) };
}
